import { inject } from 'inversify'
import Debug from 'debug';
import { ok } from '@xlearn/common'
import { QuizPlayerStateMachineAbstractState, QuizPlayerStateMachineCommonEvents } from './quiz-player-state-machine-state'
import { QuizPlayerStateMachineData } from '../data';
import { LoadQuizJob } from './load-quiz-job';
import { CancelToken } from '@/app/shared/command-queue';
import { QuizSessionQuerySymbol, QuizSessionQuery } from '@/app/quiz-session/store';
import { waitToLoad } from '@/app/shared/utils';

const debug = Debug('quiz-player-loading-state')

export interface LoadingFoundSession {
  id: string,
  type: string // e.g. exam or practice
  state: unknown
}

interface Events extends QuizPlayerStateMachineCommonEvents {
  onLoaded: () => void
  onError: (err: unknown) => void
  onActiveSessionFound: (session: LoadingFoundSession) => void
}

export class QuizPlayerLoadingState extends QuizPlayerStateMachineAbstractState<Events> {

  public readonly name = 'loading'

  constructor(
    private quizId: string,
    private aspect: string,
    private data: QuizPlayerStateMachineData,
    private loadQuizJob: LoadQuizJob,
    @inject(QuizSessionQuerySymbol)
    private quizSessionQuery: QuizSessionQuery) {

    super()
  }

  enter() {
    debug('enter')
    const startJob = this.start(this.cancelToken)
    startJob.catch(err => {
      debug('startJob error', err)
      this.triggerEvent(events => events.onError(err))
    })
  }

  exit() {
    debug('exit')
  }

  cancel() {
    debug('cancel')
    this.beginCancel()
  }

  private async start(cancelToken: CancelToken) {
    const result = await this.loadQuizAndCheckActiveSession(cancelToken)

    const isCanceled = cancelToken.cancel ||
          (!result.success && result.error === 'canceled')

    if (isCanceled) {
      debug('endCancel')
      this.endCancel()
      return
    }
    
    if (!result.success) {
      this.triggerEvent(events => events.onError(result.error))
      return
    }

    const activeSession = result.value.foundSession
    
    if (activeSession) {
      debug('active session found', activeSession)
      this.triggerEvent(events => events.onActiveSessionFound(activeSession))
      return
    }

    this.triggerEvent(events => events.onLoaded())
  }

  private async loadQuizAndCheckActiveSession(cancelToken: CancelToken) {

    const detailsResult = await this.loadQuizDetails(cancelToken)
    if (!detailsResult.success) {
      return detailsResult
    }

    const details = detailsResult.value
    debug('quiz details loaded', details)

    this.data.loaded(details)

    const activeSessionResult = await this.checkActiveSession(this.quizId, cancelToken)
    if (!activeSessionResult.success) {
      return activeSessionResult
    }

    const activeSession = activeSessionResult.value
    return ok({
      foundSession: activeSession
    })
  }

  private async loadQuizDetails(cancelToken: CancelToken) {
    const result = await this.loadQuizJob.execute(this.quizId, cancelToken)
    if (!result.success) {
      return result
    }

    const quiz = result.value
    debug('loading quiz details', quiz)
    
    let practiceDetails
    if (quiz.hasPractice) {
      practiceDetails = {
        hasPractice: true as true,
        practice: {
          questionCount: quiz.practice.questionCount,
          description: quiz.practice.description,
          feedback: quiz.practice.feedback
        }
      }
    } else {
      practiceDetails = {
        hasPractice: false as false
      }
    }

    let examDetails
    if (quiz.hasExam) {
      examDetails = {
        hasExam: true as true,
        exam: {
          percentToPass: quiz.exam.percentToPass,
          description: quiz.exam.description,
          feedback: quiz.exam.feedback
        }
      } }
    else {
      examDetails = {
        hasExam: false as false
      }
    }

    const details = {
      quizId: this.quizId,
      aspect: this.aspect,
      ...practiceDetails,
      ...examDetails
    }
    return ok(details)
  }

  private async checkActiveSession(quizId: string, cancel: CancelToken) {
    debug('waiting for remote sessions to load', quizId)
    const options = { delayMs: 40, timeoutMs: 1000 }
    const result = await waitToLoad(() => this.quizSessionQuery.remotePhase === 'loaded', cancel, options)
    debug('remote sessions loaded', result)
    if (!result.success && result.error !== 'timeout') {
      return result
    }

    const found = this.quizSessionQuery.findSessionByQuizId(quizId, this.aspect)
    return ok(found)
  }
}
